import { Check, Clock } from "lucide-react";
import { useSelector } from "@xstate/react";
import type { ActorRefFrom } from "xstate";
import { tv } from "tailwind-variants";
// FSD VIOLATION: Shared UI component needs entity type for transfers lab artwork state machine.
// eslint-disable-next-line no-restricted-imports
import type { transferArtworkMachine } from "@design-lab/entities/transfers-lab/model/transfer-artwork.machine";

type ArtworkListProps = {
  items: ActorRefFrom<typeof transferArtworkMachine>[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  badgeType?: "review" | "quantity";
};

export function ArtworkList({
  items,
  selectedId,
  onSelect,
  badgeType = "review"
}: ArtworkListProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-3">
      {items.map(item => (
        <ArtworkCard
          key={item.id}
          item={item}
          isSelected={selectedId === item.id}
          onSelect={() => onSelect(item.id)}
          badgeType={badgeType}
        />
      ))}
    </div>
  );
}

type ArtworkCardProps = {
  item: ActorRefFrom<typeof transferArtworkMachine>;
  isSelected: boolean;
  onSelect: () => void;
  badgeType?: "review" | "quantity";
};

export function ArtworkCard({
  item,
  isSelected,
  onSelect,
  badgeType = "review"
}: ArtworkCardProps) {
  const snapshot = useSelector(item, s => s);
  const { artwork, isReviewed, quantity } = snapshot.context;

  // Get image URL from artwork
  const imageUrl = artwork?.noBgUrl || artwork?.noBgNoTrimUrl || artwork?.originalImageUrl || "";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={artworkCardStyles({ selected: isSelected, reviewed: isReviewed })}
      data-testid={`artwork-${item.id}`}
    >
      {/* Image with opacity for non-reviewed items */}
      <div className={imageContainerStyles({ reviewed: isReviewed })}>
        {imageUrl ? (
          <img src={imageUrl} alt="Artwork" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
            No preview
          </div>
        )}
      </div>

      {/* Status indicator or quantity badge */}
      <div className="absolute -right-[3px] -top-[3px]">
        {badgeType === "quantity" ? (
          <span
            className={quantityBadgeStyles({ hasQuantity: quantity > 0 })}
            aria-label={`Quantity ${quantity}`}
          >
            {quantity}
          </span>
        ) : (
          <>
            {isReviewed ? (
              <div className="flex size-5 items-center justify-center rounded-full bg-blue-600">
                <Check className="size-3.5 text-white" strokeWidth={1.5} />
              </div>
            ) : (
              <div className="flex size-5 items-center justify-center rounded-full bg-amber-500">
                <Clock className="size-4 text-gray-900" strokeWidth={1.5} />
              </div>
            )}
          </>
        )}
      </div>
    </button>
  );
}

const artworkCardStyles = tv({
  base: "group relative size-[68px] cursor-pointer rounded-lg outline outline-[3px] outline-gray-200 transition-all hover:outline-gray-300",
  variants: {
    selected: {
      true: "outline-blue-700"
    },
    reviewed: {
      false: "opacity-70"
    }
  },
  compoundVariants: [
    {
      selected: true,
      reviewed: false,
      className: "opacity-100"
    }
  ]
});

const imageContainerStyles = tv({
  base: "size-full overflow-hidden rounded-lg",
  variants: {
    reviewed: {
      false: "opacity-60"
    }
  }
});

const quantityBadgeStyles = tv({
  base: "flex h-5 min-w-5 items-center justify-center rounded-lg px-1 text-sm font-semibold text-white",
  variants: {
    hasQuantity: {
      true: "bg-blue-700",
      false: "bg-black"
    }
  }
});
